import React from "react";
import { PROPERTY_TYPE, TPropertyLocalized } from "properties/_models/PropertyMdl";
import {
    CardGalleryLabelPhase,
    CardGalleryLabelPromotion,
    CardGalleryLabelType,
} from "properties/propertyCard/components/PropertyCardComponents";
import { useTranslation } from "react-i18next";
import { CardFavoriteButton } from "properties/propertyCard/components/CardFavoriteButton";
import { NoSsr } from "@material-ui/core";
import { LazyLoadImage } from "react-lazy-load-image-component";
import clsx from "clsx";
import { COMMON_RESOURCE } from "_common/resources/BaseResourceStore";
import { getLabelOfPhase } from "_common/_utils/getLabelOfPhase";
import { getEarliestAvailablePhase } from "_common/_utils/inventoryUtils";
import { TImage } from "_common/types/baseTypes";
import { TPhasesMdl } from "admin/phases/_models/PhasesMdl";
import Suspenser from "_common/loaders/Suspenser";
import PropertyFavoriteCardCheckbox from "properties/propertyCard/components/favorite/property-favorite-card-checkbox";

interface Props {
    property: {
        _id: string;
        photos: TImage[];
        localized: {
            title: TPropertyLocalized["title"];
            discountText?: TPropertyLocalized["discountText"];
        };
        type: PROPERTY_TYPE;
        phases: TPhasesMdl[];
        discount: boolean;
    };
    isLuxury?: boolean;
    isFavoriteCard?: boolean;
}

export function PropertyCardGallery({ property, isLuxury, isFavoriteCard }: Props) {
    const { t } = useTranslation();

    return (
        <div className="w-full overflow-hidden h-[277px] group">
            <LazyLoadImage
                src={property.photos?.[1]?.url}
                alt={"property image"}
                title={`${property.localized.title} photo 2`}
                width="300"
                height="200"
                loading="lazy"
                className="absolute top-0 left-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out opacity-0 group-hover:opacity-100 rounded-lg"
                onError={({ currentTarget }) => {
                    currentTarget.onerror = null; // prevents looping
                    // currentTarget.src = new URL(`https://zisla.com/${property.photos?.[1]?.url}`).href;
                    currentTarget.src = `https://placehold.co/300x200?text=Zisla.com`;
                }}
            />

            <LazyLoadImage
                src={property.photos?.[0]?.url}
                alt={"property image"}
                title={`${property.localized.title} photo 1`}
                width="300"
                height="200"
                className="absolute top-0 left-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out opacity-100 group-hover:opacity-0 rounded-lg"
                onError={({ currentTarget }) => {
                    currentTarget.onerror = null; // prevents looping
                    // currentTarget.src = new URL(`https://zisla.com/${property.photos?.[0]?.url}`).href;
                    currentTarget.src = `https://placehold.co/300x200?text=Zisla.com`;
                }}
            />

            <Suspenser>
                <CardGalleryLabelType
                    type={t("property.typeLabels." + property.type)}
                    className={clsx({ "-rotate-90 w-[80px] h-[30px] pr-5 pl-1 -left-2 top-6": isLuxury })}
                />
            </Suspenser>

            {property?.phases.length && (
                <CardGalleryLabelPhase type={getLabelOfPhase(getEarliestAvailablePhase(property.phases))} />
            )}

            {property?.discount && (
                <CardGalleryLabelPromotion
                    type={
                        property?.localized.discountText
                            ? property.localized.discountText
                            : t("admin.property.discount.isActive")
                    }
                />
            )}

            <NoSsr>
                {isFavoriteCard && <PropertyFavoriteCardCheckbox propertyId={property._id} itemType={property.type} />}
                <CardFavoriteButton itemId={property._id} resource={COMMON_RESOURCE.PROPERTIES} />
            </NoSsr>
        </div>
    );
}
